import { Trans, useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import ModalConfirmationContent from '../../../../shared/components/modal-confirmation-content/modal-confirmation-content';
import Modal from '../../../../shared/components/modal/modal';
import { useConfirmationError } from '../../../../shared/hooks/use-confirmation-error';
import { useAppSelector } from '../../../../store/hooks';
import LazyModal, { LazyModalComponentProps } from '../../../ui/components/lazy-modal';
import { closeModal, setAlert } from '../../../ui/store/ui-slice';
import { useDeleteCustomerMutation, useGetCustomerQuery } from '../../api/customers-api';
import { customerFormDeleteModalId, customersAlertId } from '../../constants/constants';
import { selectActiveCustomerId, setActiveCustomerId } from '../../store/customers-slice';

const CustomerDeleteModalContent = ({ isOpen, afterClose }: LazyModalComponentProps): JSX.Element => {
  const { t } = useTranslation(['customers', 'common']);
  const dispatch = useDispatch();
  const { confirmationError, addConfirmationError, resetConfirmationError } = useConfirmationError();
  const activeCustomerId = useAppSelector(selectActiveCustomerId);
  const [deleteCustomer, { isLoading: isDeleteCustomerLoading }] = useDeleteCustomerMutation();

  const { data: customerData } = useGetCustomerQuery(activeCustomerId || '', {
    skip: !activeCustomerId,
  });

  const close = () => {
    dispatch(closeModal(customerFormDeleteModalId));
    dispatch(setActiveCustomerId(null));
  };

  const handleOk = () => {
    if (!activeCustomerId) {
      return;
    }

    resetConfirmationError();

    deleteCustomer(activeCustomerId)
      .unwrap()
      .then(() => {
        dispatch(
          setAlert({
            id: customersAlertId,
            type: 'success',
            title: t('customerDeleteModal.message.successDeleteMessage'),
          }),
        );
        close();
      })
      .catch(() => addConfirmationError(t('errors.deleteFailed', { ns: 'common' })));
  };

  return (
    <Modal
      title={t('customerDeleteModal.deleteCustomer')}
      onCancel={close}
      isLoading={isDeleteCustomerLoading}
      customSubmitText={t('action.delete', { ns: 'common' })}
      onOk={handleOk}
      isOpen={isOpen}
      afterClose={afterClose}
    >
      {customerData?.name && (
        <ModalConfirmationContent
          confirmDeleteText={
            <Trans
              i18nKey="customerDeleteModal.confirmationDelete"
              ns="customers"
              t={t}
              values={{ name: customerData.name }}
            />
          }
          confirmationError={confirmationError}
        />
      )}
    </Modal>
  );
};

const CustomerDeleteModal = () => {
  return <LazyModal modalId={customerFormDeleteModalId} Component={CustomerDeleteModalContent} />;
};

export default CustomerDeleteModal;
